import { Helmet } from "react-helmet-async";
import { buildBreadcrumbJsonLd } from "@/lib/seo-schemas";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Check, Star, Crown, Rocket, Shield, Gift, CreditCard, Play } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useAffiliateTracking } from "@/hooks/useAffiliateTracking";

const AbonnementIptv = () => {
  useAffiliateTracking();
  const faqData = [
    {
      question: "Comment fonctionne un abonnement IPTV ?",
      answer: "Un abonnement IPTV vous donne accès à des identifiants de connexion (username/password ou lien M3U) que vous utilisez dans une application compatible (IPTV Smarters, TiviMate, VLC...). Vous pouvez alors regarder toutes les chaînes TV, films et séries sur n'importe quel appareil connecté à internet."
    },
    {
      question: "Quelle durée d'abonnement choisir ?",
      answer: "Nous recommandons l'abonnement 12 mois qui offre le meilleur rapport qualité/prix (seulement 5€/mois). Si vous préférez tester plus longtemps, le 3 mois à 25€ est idéal. Vous pouvez aussi faire un test gratuit 24h avant de vous décider."
    },
    {
      question: "Comment recevoir mon abonnement IPTV ?",
      answer: "Après paiement, vous recevez automatiquement vos identifiants par email en moins de 5 minutes. L'email contient vos codes d'accès et les instructions d'installation pour tous les appareils."
    },
    {
      question: "Puis-je utiliser l'abonnement sur plusieurs appareils ?",
      answer: "Oui, votre abonnement peut être configuré sur plusieurs appareils (Smart TV, smartphone, tablette, PC), mais une seule connexion simultanée est possible. Pour plusieurs écrans en même temps, contactez-nous pour une offre multi-écrans."
    },
    {
      question: "Quels moyens de paiement acceptez-vous ?",
      answer: "Nous acceptons les cartes bancaires (Visa, Mastercard, Amex) via Stripe, une plateforme de paiement 100% sécurisée. Vos données bancaires ne sont jamais stockées chez nous."
    },
    {
      question: "Y a-t-il un engagement sur l'abonnement ?",
      answer: "Non, aucun engagement ! Vous choisissez la durée qui vous convient (3, 6 ou 12 mois) et vous n'êtes pas reconduit automatiquement. À la fin de votre période, vous décidez librement de renouveler ou non."
    },
    {
      question: "Que se passe-t-il si mon abonnement ne fonctionne pas ?",
      answer: "Nos comptes sont garantis. En cas de problème technique, notre support réactif vous aide à résoudre le souci. Si le compte est défaillant, nous le remplaçons immédiatement sans frais."
    },
    {
      question: "L'abonnement IPTV inclut-il le sport ?",
      answer: "Oui ! Tous nos abonnements incluent l'accès complet au sport en direct : Ligue 1, Champions League, Premier League, beIN Sports, RMC Sport, Canal+ Sport, DAZN, Eurosport, UFC, F1, et bien plus."
    }
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Abonnement IPTV Premium - RoiDuStreaming",
    "description": "Abonnement IPTV avec +15 000 chaînes, +100 000 films, +25 000 séries, sport en 4K. Dès 25€ pour 3 mois. Sans engagement.",
    "image": [
      "https://roidustreaming.fr/logos/logo-netflix-streaming.webp",
      "https://roidustreaming.fr/logos/logo-disney-plus-streaming.webp",
      "https://roidustreaming.fr/logos/logo-amazon-prime-video.webp",
      "https://roidustreaming.fr/logos/logo-canal-plus-tv.webp"
    ],
    "brand": { "@type": "Brand", "name": "RoiDuStreaming" },
    "offers": {
      "@type": "AggregateOffer",
      "offerCount": 3,
      "lowPrice": "25",
      "highPrice": "60",
      "priceCurrency": "EUR",
      "offers": [
        {
          "@type": "Offer",
          "name": "Abonnement 3 Mois",
          "price": "25",
          "priceCurrency": "EUR",
          "availability": "https://schema.org/InStock",
          "priceValidUntil": "2026-12-31",
          "url": "https://roidustreaming.fr/abonnement-iptv",
          "hasMerchantReturnPolicy": {
            "@type": "MerchantReturnPolicy",
            "applicableCountry": "FR",
            "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
            "merchantReturnDays": 14,
            "returnMethod": "https://schema.org/ReturnByMail",
            "returnFees": "https://schema.org/FreeReturn"
          },
          "shippingDetails": {
            "@type": "OfferShippingDetails",
            "shippingRate": {
              "@type": "MonetaryAmount",
              "value": "0",
              "currency": "EUR"
            },
            "shippingDestination": {
              "@type": "DefinedRegion",
              "addressCountry": "FR"
            },
            "deliveryTime": {
              "@type": "ShippingDeliveryTime",
              "handlingTime": {
                "@type": "QuantitativeValue",
                "minValue": 0,
                "maxValue": 0,
                "unitCode": "h"
              },
              "transitTime": {
                "@type": "QuantitativeValue",
                "minValue": 0,
                "maxValue": 1,
                "unitCode": "h"
              }
            }
          }
        },
        {
          "@type": "Offer",
          "name": "Abonnement 6 Mois",
          "price": "40",
          "priceCurrency": "EUR",
          "availability": "https://schema.org/InStock",
          "priceValidUntil": "2026-12-31",
          "url": "https://roidustreaming.fr/abonnement-iptv",
          "hasMerchantReturnPolicy": {
            "@type": "MerchantReturnPolicy",
            "applicableCountry": "FR",
            "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
            "merchantReturnDays": 14,
            "returnMethod": "https://schema.org/ReturnByMail",
            "returnFees": "https://schema.org/FreeReturn"
          },
          "shippingDetails": {
            "@type": "OfferShippingDetails",
            "shippingRate": {
              "@type": "MonetaryAmount",
              "value": "0",
              "currency": "EUR"
            },
            "shippingDestination": {
              "@type": "DefinedRegion",
              "addressCountry": "FR"
            },
            "deliveryTime": {
              "@type": "ShippingDeliveryTime",
              "handlingTime": {
                "@type": "QuantitativeValue",
                "minValue": 0,
                "maxValue": 0,
                "unitCode": "h"
              },
              "transitTime": {
                "@type": "QuantitativeValue",
                "minValue": 0,
                "maxValue": 1,
                "unitCode": "h"
              }
            }
          }
        },
        {
          "@type": "Offer",
          "name": "Abonnement 12 Mois",
          "price": "60",
          "priceCurrency": "EUR",
          "availability": "https://schema.org/InStock",
          "priceValidUntil": "2026-12-31",
          "url": "https://roidustreaming.fr/abonnement-iptv",
          "hasMerchantReturnPolicy": {
            "@type": "MerchantReturnPolicy",
            "applicableCountry": "FR",
            "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
            "merchantReturnDays": 14,
            "returnMethod": "https://schema.org/ReturnByMail",
            "returnFees": "https://schema.org/FreeReturn"
          },
          "shippingDetails": {
            "@type": "OfferShippingDetails",
            "shippingRate": {
              "@type": "MonetaryAmount",
              "value": "0",
              "currency": "EUR"
            },
            "shippingDestination": {
              "@type": "DefinedRegion",
              "addressCountry": "FR"
            },
            "deliveryTime": {
              "@type": "ShippingDeliveryTime",
              "handlingTime": {
                "@type": "QuantitativeValue",
                "minValue": 0,
                "maxValue": 0,
                "unitCode": "h"
              },
              "transitTime": {
                "@type": "QuantitativeValue",
                "minValue": 0,
                "maxValue": 1,
                "unitCode": "h"
              }
            }
          }
        }
      ]
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "2847"
    }
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  };

  const breadcrumbJsonLd = buildBreadcrumbJsonLd([
    { name: "Accueil", href: "/" },
    { name: "Abonnement IPTV", href: "/abonnement-iptv" },
  ]);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Abonnement IPTV 2025 - Dès 25€ | +15 000 Chaînes, Films, Sport 4K</title>
        <meta name="description" content="Abonnement IPTV premium dès 25€. Accédez à +15 000 chaînes TV, +100 000 films, +25 000 séries, sport en 4K. Test gratuit 24h. Sans engagement. Livraison immédiate." />
        <meta name="keywords" content="abonnement iptv, iptv abonnement, souscrire iptv, acheter iptv, iptv premium, iptv 12 mois" />
        <link rel="canonical" href="https://roidustreaming.fr/abonnement-iptv" />
        <meta property="og:title" content="Abonnement IPTV 2025 - Dès 25€ | Premium" />
        <meta property="og:description" content="Abonnement IPTV premium avec +15 000 chaînes, films, sport 4K. Test gratuit 24h." />
        <meta property="og:url" content="https://roidustreaming.fr/abonnement-iptv" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <Header />
      <Breadcrumbs />

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-b from-primary/10 to-background">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-secondary/20 text-secondary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Gift className="w-4 h-4" />
            <span>Offre Spéciale : -40% sur l'abonnement 12 mois</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="gradient-text">Abonnement IPTV</span> Premium
            <br />Tout Inclus dès 25€
          </h1>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Obtenez un accès illimité à <strong className="text-foreground">+15 000 chaînes TV</strong>, <strong className="text-foreground">+100 000 films</strong>, 
            <strong className="text-foreground"> +25 000 séries</strong> et tout le <strong className="text-foreground">sport en direct 4K</strong>. Livraison immédiate par email, sans engagement.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/nos-offres">
              <Button size="lg" className="text-lg px-8 py-6 btn-primary text-primary-foreground">
                <CreditCard className="w-5 h-5 mr-2" />
                Choisir Mon Abonnement
              </Button>
            </Link>
            <Link to="/#pricing">
              <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-primary/50 hover:bg-primary/10">
                <Play className="w-5 h-5 mr-2" />
                Test Gratuit 24h
              </Button>
            </Link>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-secondary" />
              <span>Livraison immédiate</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-secondary" />
              <span>Paiement sécurisé</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-secondary" />
              <span>Sans engagement</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-secondary" />
              <span>Support 7j/7</span>
            </div>
          </div>
        </div>
      </section>

      {/* Offres */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Nos <span className="gradient-text">Abonnements IPTV</span>
          </h2>
          <p className="text-center text-muted-foreground mb-12">
            Choisissez la durée qui vous convient - Tous les abonnements incluent l'accès complet
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {/* 3 Mois */}
            <div className="card-premium p-8 rounded-2xl relative">
              <h3 className="text-2xl font-bold mb-2 text-primary">3 MOIS</h3>
              <p className="text-muted-foreground mb-6">90 JOURS - ABONNEMENT</p>
              <div className="mb-6">
                <span className="text-4xl font-bold">25€</span>
                <p className="text-sm text-muted-foreground">soit 8,33€/mois</p>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2">
                  <span className="text-secondary">✅</span>
                  <span>+15 000 CHAÎNES TV</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-secondary">✅</span>
                  <span>+100 000 FILMS</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-secondary">✅</span>
                  <span>+25 000 SÉRIES</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-secondary">✅</span>
                  <span>FONCTIONNE PARTOUT</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-secondary">✅</span>
                  <span>SUPPORT 7J/7</span>
                </li>
              </ul>
              <Link to="/nos-offres" className="block">
                <Button className="w-full btn-primary text-primary-foreground" size="lg">CHOISIR</Button>
              </Link>
            </div>

            {/* 6 Mois */}
            <div className="card-premium p-8 rounded-2xl relative border-2 border-primary glow-primary">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gold text-gold-foreground px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                <Crown className="w-3 h-3" />
                POPULAIRE
              </div>
              <h3 className="text-2xl font-bold mb-2 text-primary">6 MOIS</h3>
              <p className="text-muted-foreground mb-6">180 JOURS - ABONNEMENT</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-primary">40€</span>
                <p className="text-sm text-secondary font-medium">soit 6,67€/mois</p>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2">
                  <span className="text-secondary">✅</span>
                  <span>+15 000 CHAÎNES TV</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-secondary">✅</span>
                  <span>+100 000 FILMS</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-secondary">✅</span>
                  <span>+25 000 SÉRIES</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-secondary">✅</span>
                  <span>FONCTIONNE PARTOUT</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-secondary">✅</span>
                  <span>SUPPORT 7J/7</span>
                </li>
              </ul>
              <Link to="/nos-offres" className="block">
                <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" size="lg">
                  <Crown className="w-4 h-4 mr-2" />
                  CHOISIR
                </Button>
              </Link>
            </div>

            {/* 12 Mois */}
            <div className="card-premium p-8 rounded-2xl relative border-2 border-secondary">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-secondary text-secondary-foreground px-4 py-1 rounded-full text-sm font-bold">
                MEILLEUR PRIX
              </div>
              <h3 className="text-2xl font-bold mb-2 text-primary">12 MOIS</h3>
              <p className="text-muted-foreground mb-6">365 JOURS - ABONNEMENT</p>
              <div className="mb-6">
                <span className="text-4xl font-bold">60€</span>
                <p className="text-sm text-secondary font-medium">soit 5€/mois - Économisez 40%</p>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2">
                  <span className="text-secondary">✅</span>
                  <span>+15 000 CHAÎNES TV</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-secondary">✅</span>
                  <span>+100 000 FILMS</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-secondary">✅</span>
                  <span>+25 000 SÉRIES</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-secondary">✅</span>
                  <span>FONCTIONNE PARTOUT</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-secondary">✅</span>
                  <span>SUPPORT VIP 24/7</span>
                </li>
              </ul>
              <Link to="/nos-offres" className="block">
                <Button className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground" size="lg">
                  <Rocket className="w-4 h-4 mr-2" />
                  CHOISIR
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section className="py-16 px-4 bg-card">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Les avantages de notre <span className="gradient-text">abonnement IPTV</span>
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Rocket className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-2">Activation Instantanée</h3>
              <p className="text-muted-foreground">Recevez vos identifiants par email en moins de 5 minutes après paiement</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-2">Paiement Sécurisé</h3>
              <p className="text-muted-foreground">Paiement via Stripe, plateforme utilisée par des millions d'entreprises</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-2">Qualité Premium</h3>
              <p className="text-muted-foreground">Streaming en 4K HDR sans lag, serveurs optimisés haute performance</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Questions sur l'<span className="gradient-text">Abonnement IPTV</span>
          </h2>

          <Accordion type="single" collapsible className="space-y-4">
            {faqData.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="card-premium border-none rounded-xl overflow-hidden"
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline text-left font-semibold hover:bg-primary/5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 px-4 bg-card">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Prêt à souscrire votre <span className="gradient-text">abonnement IPTV</span> ?
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Rejoignez des milliers de clients satisfaits et profitez du meilleur du streaming.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/nos-offres">
              <Button size="lg" className="text-lg px-8 py-6 btn-primary text-primary-foreground">
                <CreditCard className="w-5 h-5 mr-2" />
                Voir les Offres
              </Button>
            </Link>
            <Link to="/#pricing">
              <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-primary/50 hover:bg-primary/10">
                Test Gratuit 24h
              </Button>
            </Link>
          </div>

          {/* Liens internes SEO */}
          <div className="mt-12 pt-8 border-t border-border">
            <p className="text-sm text-muted-foreground mb-4">Découvrez aussi :</p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/iptv-france" className="text-primary hover:underline">IPTV France</Link>
              <Link to="/iptv-pas-cher" className="text-primary hover:underline">IPTV Pas Cher</Link>
              <Link to="/tutoriels" className="text-primary hover:underline">Tutoriels Installation</Link>
              <Link to="/avis-clients" className="text-primary hover:underline">Avis Clients</Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AbonnementIptv;
